import React, {useEffect, useMemo, useState} from 'react';
import {Button, Col, Divider, Form, Modal, Row, Select} from "antd";
import {StorageBoxIcon} from "@components/icons/Icons";
import {
    useCollectionAssignLocationMutation, 
    useSeriesAssignLocationMutation,
    useGetCollectionLocationsQuery
} from "@mwServices/User";
import {
    useBoxAssignLocationMutation
} from "@mwServices/Boxes";
import {showResponse} from "@services/Api";
import {WarningOutlined} from "@ant-design/icons";
import {Issue, useRemoveLocationFromCollectionMutation} from "@mwServices/Issue";
import {stopClickEvent} from "@lib/utils";

interface AssignLocationModalProps {
    isModalOpen: boolean;
    onCancel: () => void;
    selectedComicIds?: number[];
    selectedSeriesIds?: number[];
    selectedBoxIds?: number[];
}
const AssignLocationModal = ({isModalOpen, onCancel, selectedComicIds, selectedSeriesIds, selectedBoxIds}: AssignLocationModalProps) => {
    const [assignIssueLocation, assignIssueLocationResponse] = useCollectionAssignLocationMutation();
    const [assignSeriesLocation, assignSeriesLocationResponse] = useSeriesAssignLocationMutation();
    const [assignBoxLocation, assignBoxLocationResponse] = useBoxAssignLocationMutation();
    const locationsQuery = useGetCollectionLocationsQuery({});
    const [postRemoveLocation, removeLocationResponse] = useRemoveLocationFromCollectionMutation();
    const [locations, setLocations] = useState<{ location: string, count: number | string }[]>([]);
    const [form] = Form.useForm();
    const [locationSearch, setLocationSearch] = useState("");
    const maxLocationLength = 100;

    useEffect(() => {
        showResponse(assignIssueLocationResponse, 'Location set.', 'Sorry, there was a problem setting the location on the issues.');
        onCancel();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [assignIssueLocationResponse]);

    useEffect(() => {
        showResponse(assignSeriesLocationResponse, 'Location set.', 'Sorry, there was a problem setting the location on the issues.');
        onCancel();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [assignSeriesLocationResponse]);

    useEffect(() => {
        showResponse(assignBoxLocationResponse, 'Location set.', 'Sorry, there was a problem setting the location on the issues.');
        onCancel();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [assignBoxLocationResponse]);

    useEffect(() => {
        if(removeLocationResponse.data && removeLocationResponse.data.success){
            if(form.getFieldValue('location') === removeLocationResponse.data.location){
                form.setFieldValue('location', '');
            }
            locationsQuery.refetch();
        }
        showResponse(removeLocationResponse, 'Location removed.', 'Failed to remove the location from the collection.');
    }, [removeLocationResponse]);

    useEffect(() => {
        if (locationsQuery.data) {
            setLocations(locationsQuery.data);
        }
    }, [locationsQuery.data]);

    return (
        <Modal title={null} open={isModalOpen} onCancel={onCancel} centered={true} destroyOnClose={true} footer={null} className="edit-location-modal">
            <Form
                layout="horizontal"
                form={form}
                initialValues={{
                    location: 'None',
                }}
                onFinish={async (values: any) => {
                    if(selectedComicIds){
                        let sanitisedData = {
                            issue_ids: selectedComicIds,
                        };

                        sanitisedData = {...sanitisedData, ...values};
                        await assignIssueLocation(sanitisedData);
                    }
                    if(selectedSeriesIds){
                        let sanitisedData = {
                            series_ids: selectedSeriesIds,
                        };

                        sanitisedData = {...sanitisedData, ...values};
                        assignSeriesLocation(sanitisedData);
                    }
                    if(selectedBoxIds){
                        let sanitisedData = {
                            box_ids: selectedBoxIds,
                        };

                        sanitisedData = {...sanitisedData, ...values};
                        assignBoxLocation(sanitisedData);
                    }
                }}
            >
                <div className="modal-title text-center">
                    <span>Add to Storage Location</span>
                </div>
                <Divider />
                <Row>
                <Col span={24}>
                    <Form.Item label="Storage Location / Box" name="location" labelAlign="right">
                        <Select
                            showSearch
                            defaultOpen={true}
                            onSearch={(value) => {
                                let locationData = (value != '') ? value.substring(0, maxLocationLength) : locationSearch
                                if (locationsQuery.data) {
                                    setLocations([{location: locationData, count: 'new'}, ...locationsQuery.data]);
                                } else {
                                    setLocations([{location: locationData, count: 'new'}]);
                                }

                                setLocationSearch(value.substring(0, maxLocationLength));
                            }}
                            placeholder="Type Location Name"
                            searchValue={locationSearch}
                        >
                            <Select.Option value="">None</Select.Option>
                            {locations.filter(function(location) {
                                    return location.location != '[No Location]'
                                }).map((location, i) => (
                                <Select.Option
                                    key={i}
                                    value={location.location}>
                                    {location.location} ({location.count})
                                    { +location.count > 0 && (
                                        <a
                                            className="location-delete"
                                            onClick={(event) => {
                                                stopClickEvent(event);
                                                Modal.confirm({
                                                    autoFocusButton: null,
                                                    icon: <WarningOutlined />,
                                                    content: (<div>
                                                        <div><b>Remove Location</b></div>
                                                        <div className={'pad-top-10'}>
                                                            <p>Are you sure?</p>
                                                            <p>Selecting this option will permanently remove location "{location.location}" from all your comics that have been designated with this location. If you do remove this location tag, comics in this location will still remain in your collection, just not be designated as "{location.location}".</p>
                                                            <p>To remove this location from this single comic, click the Location dropdown option and select "none".</p>
                                                        </div>
                                                    </div>),
                                                    onOk() {
                                                        postRemoveLocation({location: location.location});
                                                    },
                                                })
                                            }}
                                        >
                                            X
                                        </a>
                                    )}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Col>
                </Row>
                <Divider />
                <div className="text-center">
                    <Button type="primary"  shape="round"  size="large" htmlType="submit">
                        SUBMIT
                    </Button><br/>
                    <Button type="link" onClick={onCancel} >CANCEL</Button>
                </div>
            </Form>
        </Modal>
    );
};

export default AssignLocationModal;